import { FC, useState } from 'react'

import {
  Text,
  IconCircleX,
  BorderRadius8,
  Checkbox,
  GreyGrey20,
  GreyGrey80,
  GreyGrey90,
  Primary13Regular,
  Primary13SemiBold,
  IconSignature,
  SimpleModal,
  Flex
} from '@sequencehq/core-components'
import { inflect } from 'inflection'
import { ModalWithEsignatureProps } from './types'
import { ApprovalFields } from '../ApprovalFields'
import { RecipientsSection } from '../RecipientsSection'
import { publishSubmitData } from '../publishSubmit'
import {
  MODAL_BASE_PROPS,
  PUBLISH_MODAL_COPY
} from '../PublishQuoteModalV2.constants'

export const ModalRepublishReadyToSignPartiallySigned: FC<
  ModalWithEsignatureProps & {
    allSignersCount: number
    signedSignersCount: number
  }
> = ({
  onSubmit,
  allSignersCount,
  signedSignersCount,
  isApprovalRequired,
  approvalComment,
  setApprovalComment,
  message,
  setMessage,
  recipients,
  ...props
}) => {
  const [confirmChecked, setConfirmChecked] = useState<boolean>(false)

  return (
    <SimpleModal
      {...MODAL_BASE_PROPS}
      {...props}
      data-testid="modal.publishQuote.republishReadyToSignPartiallySigned"
      title={
        isApprovalRequired
          ? PUBLISH_MODAL_COPY.approvalRequired.title
          : PUBLISH_MODAL_COPY.publishChanges.title
      }
      onSubmit={async () => {
        await onSubmit(
          publishSubmitData({
            recipients,
            message,
            approvalComment,
            isApprovalRequired
          })
        )

        return true
      }}
      isDestructive
      submitDisabled={!confirmChecked}
      submitButtonText={
        isApprovalRequired
          ? PUBLISH_MODAL_COPY.approvalRequired.submitButton
          : 'Revoke and publish'
      }
      submittingButtonText={
        isApprovalRequired
          ? PUBLISH_MODAL_COPY.approvalRequired.submittingButton
          : PUBLISH_MODAL_COPY.publishQuote.submittingButton
      }
    >
      <Flex flexDir="column" gap="16px">
        {isApprovalRequired && (
          <Text {...Primary13Regular} color={GreyGrey80}>
            {PUBLISH_MODAL_COPY.approvalRequired.body}
          </Text>
        )}

        {isApprovalRequired && (
          <ApprovalFields
            data-testid="modal.publishQuote.approvalComment"
            approvalComment={approvalComment}
            setApprovalComment={value => setApprovalComment(value)}
          />
        )}

        <RecipientsSection
          recipients={recipients}
          message={message}
          setMessage={setMessage}
          submitting={props.submitting}
        />

        <Text
          data-testid="modal.publishQuote.republishReadyToSignPartiallySigned.warning"
          {...Primary13Regular}
          color={GreyGrey80}
        >
          This quote has been partially signed. Publishing changes will:
        </Text>

        <Flex flexDir="column">
          <Flex padding="8px" gap="8px">
            <IconSignature color={GreyGrey80} />
            <Text {...Primary13Regular} color={GreyGrey80}>
              Revoke{' '}
              <Text as="span" {...Primary13SemiBold} color={GreyGrey80}>
                {signedSignersCount} of {allSignersCount} signatures
              </Text>{' '}
              collected
            </Text>
          </Flex>

          <Flex padding="8px" gap="8px">
            <IconCircleX color={GreyGrey80} />
            <Text {...Primary13Regular} color={GreyGrey80}>
              This action{' '}
              <Text as="span" {...Primary13SemiBold} color={GreyGrey80}>
                can&apos;t be undone
              </Text>
            </Text>
          </Flex>
        </Flex>

        <Flex
          paddingX="10px"
          paddingY="12px"
          backgroundColor={GreyGrey20}
          borderRadius={BorderRadius8}
        >
          <Checkbox
            data-testid="modal.publishQuote.republishReadyToSignPartiallySigned.confirmCheckbox"
            isChecked={confirmChecked}
            onChange={e => setConfirmChecked(e.target.checked)}
            label={
              <Text {...Primary13Regular} color={GreyGrey90}>
                Revoke {signedSignersCount} existing{' '}
                {inflect('signature', signedSignersCount)}
              </Text>
            }
          />
        </Flex>
      </Flex>
    </SimpleModal>
  )
}
